import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AuthSessionResponse } from '@entities/user/api';
import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';

/** Действующая редакция правового документа (`GET /auth/legal-documents`). */
export interface LegalDocument {
  id: string;
  document_type: 'terms' | 'privacy';
  version: string;
  locale: string;
  effective_at: string;
}

/** Ответ `POST /auth/code/request`. Кода в нём нет — он только в письме. */
export interface AuthCodeRequested {
  challenge_id: string;
  expires_in_seconds: number;
  resend_available_in_seconds: number;
}

export interface RequestCodeInput {
  email: string;
  locale: string;
  legalDocumentIds: string[];
}

export interface VerifyCodeInput {
  challengeId: string;
  code: string;
}

/**
 * Документы для формы входа.
 *
 * Пустой список — рабочее состояние, а не ошибка: пока редакции не заведены,
 * согласие не требуется и backend его не спрашивает. Форма в этом случае
 * просто не показывает чекбокс.
 */
export function useLegalDocuments() {
  return useQuery({
    queryKey: queryKeys.legalDocuments(),
    queryFn: () => apiFetch<LegalDocument[]>('/auth/legal-documents'),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export function useRequestAuthCode() {
  return useMutation({
    mutationFn: ({ email, locale, legalDocumentIds }: RequestCodeInput) =>
      apiFetch<AuthCodeRequested>('/auth/code/request', {
        method: 'POST',
        body: {
          email: email.trim().toLowerCase(),
          purpose: 'login',
          locale,
          legal_document_ids: legalDocumentIds,
        },
      }),
  });
}

export function useVerifyAuthCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ challengeId, code }: VerifyCodeInput) =>
      apiFetch<AuthSessionResponse>('/auth/code/verify', {
        method: 'POST',
        body: { challenge_id: challengeId, code },
      }),
    onSuccess: (session) => {
      // Ответ verify содержит того же пользователя, что и /auth/me: кладём его
      // в кеш сразу, чтобы интерфейс не мигал состоянием «не авторизован».
      queryClient.setQueryData(queryKeys.me(), session);
    },
  });
}
